import { filter, get, isEmpty, values } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch, connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Background, Box, Button, Col, H3, Row } from '@commonsku/styles';

import ProductSearch from './ProductSearch';
import PoDSearchContainer from './product-search-pod/PoDSearchContainer';

import { createDeleteTemp } from '../actions/temp';
import { window } from '../global';
import ThemeProductContainer from './theme/ThemeProductContainer';
import SuggestionContainer from './product-suggestion/SuggestionContainer';
import ga from '../utils/ga';
import { useIdentity, useHasUserFlag } from '../hooks';
import { oauth } from '../utils';
import { hasAlgoliaConfig } from './product-suggestion/Algoliasearch';
import { trackEvent } from '../utils/analytics';

const FETCH_INTERVAL = 5000;
const MAX_RETRY_TIME = 15000;

const ProductSearchModal = ({
  project,
  order,
  params,
  isCollectionPage,
  popups = [],
  location,
  catalogId,
  clientId,
  isPoDShop,
  onClose: onCloseOverride,
  headerText: headerTextOverride,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isCatalogMode = !!catalogId;

  // Explicitly determine if we're adding to a project note vs an order.
  // Route /project/:job_number/add-product has no order_type in params;
  // /project/:job_number/:order_type/:order_number/add-product does. Never
  // true in catalog mode — there we always add to the catalog.
  const isAddingToProjectNote = !isCatalogMode && !params?.order_type && !order;

  const themes = useSelector((state) => {
    const results = values(get(state, "entities.themes", []));
    return order
      ? filter(results, (theme) => +theme.is_featured === 1)
      : results;
  });
  const [showThemes, setShowThemes] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const identity = useIdentity();
  const [isSuggestSearch, setIsSuggestSearch] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const { order_type, order_id } = order || {};
  const productSuggestionsDisabled = useHasUserFlag("PRODUCT-SUGGESTIONS-DISABLED");

  useEffect(() => {
    if (
      !order_id ||
      order_type === "SHOP" ||
      order_type === "COLLECTION" ||
      !hasAlgoliaConfig ||
      productSuggestionsDisabled
    ) {
      return;
    }

    setRecommendationsLoading(true);
    const startTime = Date.now();
    let timeoutId;
    const controller = new AbortController();

    const trackLoadingTime = (success) => {
      trackEvent("llm_suggestion loading", {
        loading_time: (Date.now() - startTime) / 1000,
        tenant_id: identity.tenant_id,
        login_email: identity.login_email,
        success,
      });
    };

    const fetchSuggestions = async () => {
      try {
        const response = await oauth(
          "GET",
          "order/get-suggestions",
          {
            order_id: order_id,
          },
          null,
          { signal: controller.signal },
          null,
          false,
        );

        if (!isEmpty(response.json.suggestions)) {
          setSuggestions(response.json.suggestions);
          setRecommendationsLoading(false);
          trackLoadingTime(true);
          return;
        }
      } catch (error) {
        if (error.name === "AbortError") return;
      }
      if (Date.now() - startTime < MAX_RETRY_TIME) {
        timeoutId = setTimeout(fetchSuggestions, FETCH_INTERVAL);
      } else {
        setRecommendationsLoading(false);
        trackLoadingTime(false);
      }
    };

    fetchSuggestions();

    return () => {
      controller.abort();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [order_type, order_id, identity.tenant_id, identity.login_email, productSuggestionsDisabled]);

  const onClose = () => {
    if (onCloseOverride) {
      onCloseOverride();
      return;
    }
    dispatch(createDeleteTemp("new_product"));
    if (window.history.length > 2) {
      navigate(-1);
    } else if (
      location &&
      location.pathname &&
      (location.pathname.endsWith("/add-product") ||
        location.pathname.endsWith("/add-product/"))
    ) {
      window.location = location.pathname.replace("/add-product", "");
    }
  };

  const headerText = headerTextOverride
    ?? (!!order && 'COLLECTION' === order_type
      ? 'Add a product to your collection'
      : 'Add Product');

  const onSelectTheme = (theme) => {
    setSelectedTheme(theme);
    ga.gtag("event", "click", {
      order_id: get(order, "order_id", ""),
      theme_id: get(theme, "theme_id", ""),
      userId: identity.user_id,
    });
  };

  return (
    <Background
      fillWindow
      padded
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Row style={{ flex: "0 0 auto" }}>
        <Col style={{ flex: 2 }}>
          <H3>{headerText}</H3>
        </Col>
        <Col style={{ flex: 1 }}>
          {!popups.length && (
            <Button onClick={onClose} style={{ float: "right" }}>
              Done
            </Button>
          )}
        </Col>
      </Row>
      {selectedTheme ? (
        <ThemeProductContainer
          order={order}
          userId={identity.user_id}
          selectedTheme={selectedTheme}
          onClose={() => setSelectedTheme(false)}
          isAddingToProjectNote={isAddingToProjectNote}
          catalogId={catalogId}
        />
      ) : isPoDShop ? (
        <PoDSearchContainer
          order={order}
          catalogId={catalogId}
          isAddingToProjectNote={isAddingToProjectNote}
        />
      ) : (
        <>
          <Box
            borderless
            style={{
              ...(showThemes
                ? { flex: "0 0 auto" }
                : { flex: 1, overflow: "auto" }),
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100% - 20px)",
            }}
          >
            <ProductSearch
              project={isCatalogMode ? (project ?? { opportunities: [] }) : project}
              order={order}
              params={isCatalogMode ? (params ?? {}) : params}
              location={location}
              isCollectionPage={isCollectionPage}
              catalogId={catalogId}
              client_id={clientId}
              onSearch={() => setShowThemes(false)}
              onClear={() => setShowThemes(true)}
              setIsSuggestSearch={setIsSuggestSearch}
              isSuggestSearch={isSuggestSearch}
              isAddingToProjectNote={isAddingToProjectNote}
            />
          </Box>
          {showThemes && (
            <SuggestionContainer
              themes={themes}
              order={order}
              catalogId={catalogId}
              onSelectTheme={onSelectTheme}
              isSuggestSearch={isSuggestSearch}
              setIsSuggestSearch={setIsSuggestSearch}
              suggestions={isCatalogMode ? [] : suggestions}
              recommendationsLoading={isCatalogMode ? false : recommendationsLoading}
              isAddingToProjectNote={isAddingToProjectNote}
              tenantId={identity.tenant_id}
            />
          )}
        </>
      )}
    </Background>
  );
};

const mapStateToProps = (state, ownProps) => ({
  identity: state.identity
});

export default connect(mapStateToProps)(ProductSearchModal);
